import { useState, useEffect } from "react";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { TESTIMONIALS } from "@/lib/content";
import { motion, AnimatePresence } from "framer-motion";

export default function TestimonialsCarousel() {
  const [i, setI] = useState(0);
  const n = TESTIMONIALS.length;

  useEffect(() => {
    const t = setInterval(() => setI((prev) => (prev + 1) % n), 7000);
    return () => clearInterval(t);
  }, [n]);

  const active = TESTIMONIALS[i];

  return (
    <section className="bg-offwhite">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <div className="overline mb-4">Client Testimonials</div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-charcoal leading-tight">The work speaks for itself.</h2>
            <div className="mt-10 flex gap-2">
              <button
                data-testid="testimonial-prev"
                aria-label="Previous"
                onClick={() => setI((i - 1 + n) % n)}
                className="w-11 h-11 border border-charcoal grid place-items-center hover:bg-charcoal hover:text-gold transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                data-testid="testimonial-next"
                aria-label="Next"
                onClick={() => setI((i + 1) % n)}
                className="w-11 h-11 border border-charcoal grid place-items-center hover:bg-charcoal hover:text-gold transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-6 font-mono text-xs tracking-[0.24em] text-charcoal-soft">
              {String(i + 1).padStart(2, "0")} <span className="mx-1 text-gray-300">/</span> {String(n).padStart(2, "0")}
            </div>
          </div>

          <div className="lg:col-span-8 lg:col-start-5">
            <AnimatePresence mode="wait">
              <motion.figure
                key={i}
                data-testid={`testimonial-slide-${i}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="bg-white border border-gray-200 p-10 md:p-14"
              >
                <Quote className="w-8 h-8 text-gold" />
                <blockquote className="mt-8 font-heading text-2xl md:text-3xl leading-snug text-charcoal">
                  "{active.quote}"
                </blockquote>
                <figcaption className="mt-10 pt-8 border-t border-gray-100 flex items-center gap-4">
                  <img src={active.avatar} alt={active.name} className="w-14 h-14 object-cover" />
                  <div>
                    <div className="font-heading font-medium text-charcoal">{active.name}</div>
                    <div className="text-sm text-charcoal-soft font-mono tracking-wide">{active.role} · {active.org}</div>
                  </div>
                </figcaption>
              </motion.figure>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
